import type { ReadScope } from "@repo/shared";
import { FileText, type LucideIcon } from "lucide-react";
import { type KeyboardEvent, type MouseEvent, useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PopoverContent, PopoverTitle } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  composeReadScopeAnswer,
  DOCUMENT_PROCESSING_COPY,
  type DocumentAnswerOutcome,
  type DocumentPreReadPopover,
  type DocumentPreReadSheet,
  type DocumentReadScopeOption,
  type DocumentReadScopeOptionKind,
  defaultReadScopeKind,
  formatSheetSelectionLabel,
} from "../lib/document-processing-view";
import { SectionHeading } from "./document-status-popover";

/** How tall the sheet list is allowed to be, whatever the workbook holds (U3). */
const SHEET_LIST_HEIGHT = "h-30";

export interface DocumentReadScopePopoverProps {
  payload: DocumentPreReadPopover;
  /** The file-type glyph the header repeats from the NAME column. */
  icon?: LucideIcon;
  /** Sends the composed answer. A refusal comes back as a sentence to show inline. */
  onSubmit: (answer: ReadScope) => Promise<DocumentAnswerOutcome>;
  /**
   * Answers the same question with "keep none of it" — see `Delete upload` below. Absent in
   * `extend` mode, where the file has already been read and deleting it is not an answer to
   * "how much more".
   */
  onDelete?: () => Promise<DocumentAnswerOutcome>;
  /** An answer for this document is already in flight. */
  isPending?: boolean;
  /** Stops a click inside the popover from reaching the row behind it. */
  onSwallowClick?: (event: MouseEvent) => void;
  onSwallowKeyDown?: (event: KeyboardEvent) => void;
}

/**
 * The question a file too large to read blind asks before anything is read (A6/A11–A13) — and,
 * in `extend` mode, the same question asked again about a document part of which is read (U11).
 *
 * Every word of it — the prompt, the counts line, which options this format even has, what
 * "Everything" promises for a 412-slide deck — was decided by `buildPreReadPopover`, so this
 * component owns exactly three things the view-model cannot: the draft the person is holding
 * (which option, which numbers, which sheets), turning that draft into a `ReadScope`, and
 * where the sheet list goes.
 *
 * The draft is **what the person last expressed**, by whichever means they expressed it. There
 * is one answer being given at a time, and the option it belongs to is the one that travels;
 * the range boxes are that option's own value, not a separate field beside it.
 *
 * The sheet list renders **only** while "Pick sheets" is chosen and is a fixed-height scroll
 * area when it does: a fifty-tab workbook must not push the two simple answers off the top of
 * the panel, and the popover must not grow past the screen.
 *
 * "Delete upload" opens no confirmation. It is one of the answers this question offers, and
 * the panel it sits in has already said what the file is and how big it is — a dialog asking
 * "are you sure" would only repeat the sentence the reader is looking at.
 */
export function DocumentReadScopePopover({
  payload,
  icon: Icon = FileText,
  onSubmit,
  onDelete,
  isPending = false,
  onSwallowClick,
  onSwallowKeyDown,
}: DocumentReadScopePopoverProps) {
  const rangeOption = useMemo(
    () => payload.options.find((option) => option.range !== undefined),
    [payload.options]
  );

  const [kind, setKind] = useState<DocumentReadScopeOptionKind>(() =>
    defaultReadScopeKind(payload)
  );
  const [start, setStart] = useState(() => String(rangeOption?.range?.defaultStart ?? ""));
  const [end, setEnd] = useState(() => String(rangeOption?.range?.defaultEnd ?? ""));
  // Nothing ticked to begin with: picking sheets is what someone does when they want a
  // handful out of many, so the shorter path is ticking three rather than unticking eleven.
  // Except in `extend`, where the sheets already read start ticked and stay ticked — they are
  // a fact about the document, not a choice still open.
  const [sheets, setSheets] = useState<readonly string[]>(() =>
    payload.sheets.filter((sheet) => sheet.alreadyRead).map((sheet) => sheet.name)
  );
  const [error, setError] = useState<string | null>(null);

  const visibleSheetNames = useMemo(
    () => payload.sheets.filter((sheet) => !sheet.hidden).map((sheet) => sheet.name),
    [payload.sheets]
  );
  // Already-read sheets survive "Clear": unticking one would be asking to unread it.
  const lockedSheetNames = useMemo(
    () => payload.sheets.filter((sheet) => sheet.alreadyRead).map((sheet) => sheet.name),
    [payload.sheets]
  );
  const allVisibleSelected =
    visibleSheetNames.length > 0 && visibleSheetNames.every((name) => sheets.includes(name));

  /**
   * The answer being given, as last expressed.
   *
   * Every way of saying an answer goes through here — pressing its radio, putting the cursor in
   * its range boxes, typing a number into them. The numbers are not a setting attached to an
   * answer chosen elsewhere: filling them in *is* choosing the range, and a panel that read the
   * radio instead would send back the answer the person had just typed over.
   */
  const choose = (next: DocumentReadScopeOptionKind) => {
    setError(null);
    setKind(next);
  };

  const toggleSheet = (name: string) => {
    if (lockedSheetNames.includes(name)) return;
    setError(null);
    setSheets((current) =>
      current.includes(name) ? current.filter((entry) => entry !== name) : [...current, name]
    );
  };

  const submit = () => {
    const composed = composeReadScopeAnswer({ kind, start, end, sheets }, payload);
    if (!composed.ok) {
      setError(composed.message);
      return;
    }
    setError(null);
    // A refusal with nothing to say is the claim turning down a second press; the panel keeps
    // the first answer's own pending state rather than reporting anything.
    void onSubmit(composed.answer).then((outcome) => {
      if (!outcome.ok && outcome.message) setError(outcome.message);
    });
  };

  const remove = () => {
    if (!onDelete) return;
    void onDelete().then((outcome) => {
      if (!outcome.ok && outcome.message) setError(outcome.message);
    });
  };

  return (
    <PopoverContent
      side="bottom"
      align="end"
      sideOffset={6}
      className="w-90 gap-0 p-0 text-[12px]"
      onClick={onSwallowClick}
      onKeyDown={onSwallowKeyDown}
      data-testid="document-read-scope-popover"
      data-format={payload.format}
      data-mode={payload.mode}
    >
      <div className="flex items-center gap-2 border-b border-border px-3.5 py-3">
        <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <PopoverTitle className="min-w-0 truncate text-[13px] font-semibold text-foreground">
          {payload.title}
        </PopoverTitle>
      </div>

      <div className="flex flex-col gap-3 px-3.5 py-3">
        <p className="text-foreground">{payload.prompt}</p>
        <p className="font-semibold tabular-nums text-foreground">{payload.header}</p>
        <SectionHeading>{payload.optionsHeading}</SectionHeading>

        <RadioGroup
          value={kind}
          onValueChange={(next) =>
            choose((next as DocumentReadScopeOptionKind | null) ?? "everything")
          }
          disabled={isPending}
          className="gap-3"
        >
          {payload.options.map((option) => (
            <ScopeOption
              key={option.kind}
              option={option}
              start={start}
              end={end}
              onStartChange={(value) => {
                choose(option.kind);
                setStart(value);
              }}
              onEndChange={(value) => {
                choose(option.kind);
                setEnd(value);
              }}
              onPickRange={() => choose(option.kind)}
              disabled={isPending}
            />
          ))}
        </RadioGroup>

        {/* Under the option it belongs to — "Pick sheets" is always last — but outside the
            radio group's own labels: a sheet row is a second control, and a tick box nested
            inside a radio's label is one click meaning two things. */}
        {kind === "sheets" ? (
          <SheetPicker
            sheets={payload.sheets}
            visibleCount={visibleSheetNames.length}
            selected={sheets}
            allVisibleSelected={allVisibleSelected}
            onToggle={toggleSheet}
            onSelectAll={() => {
              setError(null);
              setSheets(allVisibleSelected ? lockedSheetNames : visibleSheetNames);
            }}
            disabled={isPending}
          />
        ) : null}

        {error ? (
          <p role="alert" data-testid="document-read-scope-error" className="text-destructive">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border px-3.5 py-2.5">
        {onDelete ? (
          <Button type="button" size="sm" variant="outline" disabled={isPending} onClick={remove}>
            {DOCUMENT_PROCESSING_COPY.deleteUpload}
          </Button>
        ) : null}
        <Button type="button" size="sm" disabled={isPending} onClick={submit}>
          {payload.submitLabel}
        </Button>
      </div>
    </PopoverContent>
  );
}

/**
 * One answer: the control, its label, and whatever the label has to hold.
 *
 * A range option's two boxes sit *inside* the label line, which is why they are here rather
 * than under it — "Only pages 1 to 500" is one sentence, and splitting it across two rows
 * makes the numbers read as a second, separate question.
 *
 * The control is named by `aria-labelledby`, not by the `<label>` around it. Base UI's radio is
 * a `<span role="radio">` beside an `aria-hidden` `<input>`, and a native `<label>` names only
 * labelable elements — so the wrapper makes the whole option clickable and names nothing. The
 * option is "Everything" to a screen reader, and to `getByRole("radio", {name})`, only because
 * the title span is pointed at explicitly; the description line follows as `aria-describedby`,
 * which is what it is — the sentence under the answer, not part of the answer's name (LAT-982).
 */
function ScopeOption({
  option,
  start,
  end,
  onStartChange,
  onEndChange,
  onPickRange,
  disabled,
}: {
  option: DocumentReadScopeOption;
  start: string;
  end: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  /** Reaching for the boxes is choosing this answer, before a single digit has changed. */
  onPickRange: () => void;
  disabled: boolean;
}) {
  const range = option.range;
  const optionId = useId();
  const labelId = `${optionId}-label`;
  const descriptionId = `${optionId}-description`;

  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the nested RadioGroupItem is the control; the label makes the whole option clickable.
    <label className="flex cursor-pointer gap-2.5">
      <RadioGroupItem
        value={option.kind}
        className="mt-0.5"
        aria-labelledby={labelId}
        {...(option.description ? { "aria-describedby": descriptionId } : {})}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span id={labelId} className="text-[13px] font-medium text-foreground">
            {option.label}
          </span>
          {range ? (
            <>
              <Input
                type="text"
                inputMode="numeric"
                value={start}
                onChange={(event) => onStartChange(event.target.value)}
                onFocus={onPickRange}
                onKeyDown={keepArrowKeysInTheBox}
                disabled={disabled}
                aria-label={`${option.label} from`}
                className="h-6 w-14 px-1.5 text-center tabular-nums"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="text"
                inputMode="numeric"
                value={end}
                onChange={(event) => onEndChange(event.target.value)}
                onFocus={onPickRange}
                onKeyDown={keepArrowKeysInTheBox}
                disabled={disabled}
                aria-label={`${option.label} to`}
                className="h-6 w-14 px-1.5 text-center tabular-nums"
              />
            </>
          ) : null}
        </span>
        {option.description ? (
          <span id={descriptionId} className="text-muted-foreground">
            {option.description}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * An arrow key pressed in a range box moves the cursor and nothing else.
 *
 * The boxes sit inside the radio group, and a radio group is a composite: an arrow key that
 * reaches it moves focus to the next answer, and the answer focus lands on selects itself. From
 * a caret at the edge of a number — or in a box someone has just emptied to retype it — the
 * group takes the key as its own, and the panel quietly ends up holding an answer nobody gave.
 * Stopping it here leaves Tab as the way out of the boxes, which is the way out of a text field.
 */
function keepArrowKeysInTheBox(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key.startsWith("Arrow")) event.stopPropagation();
}

/**
 * The workbook's worksheets, with the two facts the choice turns on: the name and how much
 * is in it.
 *
 * Hidden sheets are listed and disabled rather than dropped. A workbook the reader knows has
 * a "Raw POS dump" tab, shown here with no tick box, says why it cannot be picked; the same
 * workbook with the tab silently missing reads as a bug.
 *
 * They are not in the count above the list, though: "3 of 14 selected" beside a Select all
 * that can only ever reach 13 is a count that reads as a bug of its own. The denominator is
 * what a person can actually tick.
 */
function SheetPicker({
  sheets,
  visibleCount,
  selected,
  allVisibleSelected,
  onToggle,
  onSelectAll,
  disabled,
}: {
  sheets: readonly DocumentPreReadSheet[];
  /** How many of them can actually be picked — the hidden ones are listed, not offered. */
  visibleCount: number;
  selected: readonly string[];
  allVisibleSelected: boolean;
  onToggle: (name: string) => void;
  onSelectAll: () => void;
  disabled: boolean;
}) {
  return (
    // Indented to the option labels above it, so the list reads as belonging to "Pick sheets".
    <div className="-mt-1 flex flex-col gap-1.5 pl-6.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="tabular-nums text-muted-foreground" data-testid="sheet-selection-count">
          {formatSheetSelectionLabel(selected.length, visibleCount)}
        </span>
        <Button
          type="button"
          size="xs"
          variant="link"
          disabled={disabled}
          onClick={onSelectAll}
          className="h-auto p-0 text-[12px]"
        >
          {allVisibleSelected
            ? DOCUMENT_PROCESSING_COPY.clearSheets
            : DOCUMENT_PROCESSING_COPY.selectAllSheets}
        </Button>
      </div>

      <ScrollArea className={cn(SHEET_LIST_HEIGHT, "border border-border")}>
        <div className="flex flex-col">
          {sheets.map((sheet) => (
            // biome-ignore lint/a11y/noLabelWithoutControl: the nested Checkbox is the control; the label makes the whole row clickable.
            <label
              key={`${sheet.index}-${sheet.name}`}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5",
                sheet.hidden || sheet.alreadyRead
                  ? "cursor-default"
                  : "cursor-pointer hover:bg-muted/40"
              )}
              title={
                sheet.hidden
                  ? "This worksheet is hidden in the workbook and can't be read."
                  : sheet.alreadyRead
                    ? "This worksheet has already been read."
                    : undefined
              }
            >
              <Checkbox
                checked={selected.includes(sheet.name)}
                onCheckedChange={() => onToggle(sheet.name)}
                disabled={disabled || sheet.hidden || sheet.alreadyRead}
                aria-label={sheet.name}
              />
              <span
                className={cn(
                  "min-w-0 flex-1 truncate",
                  sheet.hidden ? "text-muted-foreground" : "text-foreground"
                )}
              >
                {sheet.name}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {sheet.hidden
                  ? DOCUMENT_PROCESSING_COPY.hiddenSheet
                  : sheet.alreadyRead
                    ? DOCUMENT_PROCESSING_COPY.alreadyReadSheet
                    : sheet.rowsLabel}
              </span>
            </label>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
